import { useState } from "react";
import {
  useDataProvider,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from "ra-core";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";

import type { DealStageConfig } from "../types";
import { toStageConfigErrorMessage } from "./stageConfigForm";

/**
 * Almikia Epic EP (TASK-105): "Archivar" / "Reactivar" -- only ever shown
 * for a non-system (direccion-created) stage; a system stage shows a short
 * reason instead (`enforce_deal_stage_configs_guard` rejects archiving one
 * server-side too). Neither action ever deletes the row -- archiving only
 * sets `archived_at`, reactivating clears it -- and each asks for a
 * plain-Spanish confirmation stating that leads already in the stage stay
 * there (design-system principle: friction proportional to risk).
 */
export const StageArchiveActions = ({
  record,
}: {
  record: DealStageConfig;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const refresh = useRefresh();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  if (record.is_system) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("resources.deal_stage_configs.archive.system_reason")}
      </p>
    );
  }

  const isArchived = record.archived_at != null;

  const handleConfirm = async () => {
    setIsSaving(true);
    try {
      await dataProvider.update("deal_stage_configs", {
        id: record.id,
        data: { archived_at: isArchived ? null : new Date().toISOString() },
        previousData: record,
      });
      notify(
        isArchived
          ? "resources.deal_stage_configs.archive.reactivated"
          : "resources.deal_stage_configs.archive.archived",
        { type: "success" },
      );
      refresh();
      redirect("list", "deal_stage_configs");
    } catch (error) {
      notify(toStageConfigErrorMessage(error), { type: "error" });
    } finally {
      setIsSaving(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div>
      <Button
        type="button"
        variant={isArchived ? "default" : "destructive"}
        className="min-h-11"
        onClick={() => setConfirmOpen(true)}
      >
        {isArchived
          ? translate("resources.deal_stage_configs.action.reactivate")
          : translate("resources.deal_stage_configs.action.archive")}
      </Button>
      <Confirm
        isOpen={confirmOpen}
        loading={isSaving}
        title={
          isArchived
            ? "resources.deal_stage_configs.archive.confirm_reactivate_title"
            : "resources.deal_stage_configs.archive.confirm_archive_title"
        }
        content={
          isArchived
            ? "resources.deal_stage_configs.archive.confirm_reactivate_body"
            : "resources.deal_stage_configs.archive.confirm_archive_body"
        }
        confirm={
          isArchived
            ? "resources.deal_stage_configs.action.reactivate"
            : "resources.deal_stage_configs.action.archive"
        }
        confirmColor={isArchived ? "primary" : "warning"}
        onConfirm={handleConfirm}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
};
